import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import lawyerImg from '../../assets/lawyer_portrait.webp';

const stats = [
  { value: 100, suffix: '+', label: 'Klien',            desc: 'Klien puas dari berbagai bidang' },
  { value: 50,  suffix: '+', label: 'Kasus Selesai',    desc: 'Kasus ditangani dengan sukses' },
  { value: 10,  suffix: '+', label: 'Tahun Pengalaman', desc: 'Bertahun-tahun pengabdian hukum' },
];

function Counter({ value, suffix }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(value / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, value);
      setCount(start);
      if (start >= value) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-serif text-4xl md:text-5xl font-bold gold-text">
      {count}{suffix}
    </span>
  );
}

const fadeUp = {
  hidden:  { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] } }),
};

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="tentang" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-600/30 to-transparent" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse 60% 40% at 70% 50%, rgba(212,149,14,0.06) 0%, transparent 70%)`,
        }}
      />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <motion.div
            custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={lawyerImg}
                alt="Pendiri Kantor Hukum"
                className="w-full h-[500px] object-cover object-top"
                loading="lazy"
                decoding="async"
              />
              {/* Gold corner accent */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 via-transparent to-transparent" />
            </div>

            {/* Floating badge */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -bottom-6 -right-6 glass rounded-2xl p-5 shadow-2xl border border-gold-600/20"
            >
              <p className="font-serif text-3xl font-bold gold-text">10+</p>
              <p className="text-sm text-navy-300 mt-1">Tahun Berpengalaman</p>
            </motion.div>

            {/* Decorative lines */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-gold-600/40 rounded-tl-2xl" />
            <div className="absolute -bottom-4 right-6 w-24 h-24 border-b-2 border-r-2 border-gold-600/40 rounded-br-2xl" />
          </motion.div>

          {/* Right: Content */}
          <div className="space-y-8">
            <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}>
              <span className="inline-block text-gold-400 text-sm font-semibold tracking-widest uppercase mb-3">
                Tentang Kami
              </span>
              <h2 className="section-title mb-6">
                Komitmen Kami untuk{' '}
                <span className="gold-text">Keadilan</span>{' '}
                dan Integritas
              </h2>
              <div className="divider-gold mb-6" />
            </motion.div>

            <motion.p
              custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}
              className="section-subtitle"
            >
              Kantor Hukum kami telah berdiri dan melayani masyarakat serta pelaku usaha
              di Pontianak, Kalimantan Barat selama lebih dari satu dekade. Kami hadir
              sebagai mitra hukum yang dapat diandalkan — memberikan pendampingan hukum
              yang profesional, strategis, dan berintegritas tinggi.
            </motion.p>

            <motion.p
              custom={3} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}
              className="section-subtitle"
            >
              Dengan tim pengacara berpengalaman dan pemahaman mendalam atas hukum Indonesia,
              kami berkomitmen untuk menghadirkan solusi hukum yang tepat, efisien, dan
              terjangkau bagi setiap klien — dari individu hingga korporasi besar.
            </motion.p>

            {/* Stats */}
            <motion.div
              custom={4} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'}
              className="grid grid-cols-3 gap-4 pt-4"
            >
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className="glass-light rounded-2xl p-5 text-center border border-white/5 hover:border-gold-600/20 transition-all duration-300"
                >
                  <Counter value={s.value} suffix={s.suffix} />
                  <p className="text-white font-semibold text-sm mt-1">{s.label}</p>
                  <p className="text-navy-400 text-xs mt-1 leading-tight">{s.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
